import {
	afterNextRender,
	computed,
	effect,
	inject,
	Injectable,
	LOCALE_ID,
	signal
} from '@angular/core';
import {LocalStorageService} from 'ngx-webstorage';
import {DOCUMENT} from '@angular/common';
import {environment} from '../environments/environment';
import {DEFAULT_LOCALE,SUPPORTED_LOCALES} from '../locale-preference';
import {Meta} from '@angular/platform-browser';
import {ConsentService} from './common/consent/consent.service';

@Injectable({
	providedIn:'root'
})
export class AppService{
	private readonly document=inject(DOCUMENT);
	private readonly localeId=inject(LOCALE_ID);
	private readonly localStorageService=inject(LocalStorageService);
	private readonly consentService=inject(ConsentService);

	private readonly meta=inject(Meta);
	private readonly baseUrl=environment.siteUrl;
	readonly ogImageUrl=`${environment.siteUrl}/${this.localeId}/icons/icon-512x512.png`;
	
	/**
	 * The visitor's explicit theme choice, or `null` to follow the operating system.
	 *
	 * A plain writable signal: applying it to the DOM and mirroring it to storage are side
	 * effects and live in the effect below, not in a `computed`.
	 */
	readonly theme=signal<string|null>(this.readStoredTheme());
	
	/** The operating system preference, tracked live so the toggle's label stays truthful. */
	private readonly systemTheme=signal<string>('app-light-theme');
	
	/** What is actually on screen: the explicit choice when there is one, else the OS preference. */
	readonly resolvedTheme=computed<string>(()=>this.theme() ?? this.systemTheme());
	
	constructor(){
		effect(():void=>{
			const theme=this.theme();
			const classList=this.document.documentElement.classList;
			classList.remove('app-dark-theme','app-light-theme');
			// No class means no `color-scheme` override, which is how a visitor who has not chosen
			// follows the operating system natively — see the `html` rule in styles.scss.
			if(theme) classList.add(theme);
			// The remembered theme is a *preferences* cookie: store it only while that category is
			// granted. Reading the signal here also re-runs this effect when consent is withdrawn,
			// which is what drops an already-remembered value.
			if(theme && this.consentService.preferences()) this.localStorageService.store('theme',theme);
			else this.localStorageService.clear('theme');
		});
		
		afterNextRender(():void=>{
			const darkColorScheme=this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)');
			if(!darkColorScheme) return;
			this.systemTheme.set(darkColorScheme.matches ? 'app-dark-theme' : 'app-light-theme');
			darkColorScheme.addEventListener('change',(event:MediaQueryListEvent):void=>{
				this.systemTheme.set(event.matches ? 'app-dark-theme' : 'app-light-theme');
			});
		});
	}
	
	/** Absolute, locale-prefixed URL for an app path — what canonical/og:url and the share links need. */
	absoluteUrl(path:string):string{
		return `${this.baseUrl}/${this.localeId}${path}`;
	}

	updateSeoUrls(routerUrl:string):void{
		const path=routerUrl.split('#')[0] || '/';
		const absolute=this.absoluteUrl(path);
		this.meta.updateTag({content:absolute},'property="og:url"');
		this.updateCanonicalTag(absolute);
		this.updateHreflangTags(path);
	}

	updateHreflangTags(currentPath:string):void{
		const head=this.document.head;
		head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el:Element)=>el.remove());

		const localePattern=new RegExp(`^/(${SUPPORTED_LOCALES.join('|')})(?=/|$)`);
		const neutralPath=currentPath.replace(localePattern,'') || '/';

		for(const locale of SUPPORTED_LOCALES){
			const link=this.document.createElement('link');
			link.setAttribute('rel','alternate');
			link.setAttribute('hreflang',locale);
			link.setAttribute('href',`${this.baseUrl}/${locale}${neutralPath}`);
			head.appendChild(link);
		}

		const xDefault=this.document.createElement('link');
		xDefault.setAttribute('rel','alternate');
		xDefault.setAttribute('hreflang','x-default');
		xDefault.setAttribute('href',`${this.baseUrl}/${DEFAULT_LOCALE}${neutralPath}`);
		head.appendChild(xDefault);
	}

	updateCanonicalTag(url:string):void{
		const head=this.document.head;
		let canonical=head.querySelector('link[rel="canonical"]') as HTMLLinkElement|null;
		if(!canonical){
			canonical=this.document.createElement('link');
			canonical.setAttribute('rel','canonical');
			head.appendChild(canonical);
		}
		canonical.setAttribute('href',url);
	}
	
	/** Flips away from what is currently on screen, so the first tap always visibly changes it. */
	toggleDarkTheme():void{
		this.theme.set(this.resolvedTheme()==='app-dark-theme' ? 'app-light-theme' : 'app-dark-theme');
	}
	
	private readStoredTheme():string|null{
		return this.consentService.preferences()
			? (this.localStorageService.retrieve('theme') as string|null) ?? null
			: null;
	}
}
